import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, Table, Input, Button, Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Badge, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import { BASE_URL } from '../constants/apiConfig';
import StatsCard from './StatsCard';
import FilterPanel from './FilterPanel';
import ColumnManager from './ColumnManager';
import ApplicationRow from './ApplicationRow';
import ExportModal from './ExportModal';
import SaveDashboardModal from './SaveDashboardModal';
import ShareDashboardModal from './ShareDashboardModal';
import FinalizeCandidatesModal from './FinalizeCandidatesModal';
import Round2AssignmentModal from './Round2AssignmentModal';
import AcceptApplicationsModal from './AcceptApplicationsModal';
import SendAnnexureModal from './SendAnnexureModal';
import './Dashboard.css';

const Dashboard = () => {

	const DEFAULT_FILTERS = {
		dateRange: { start: '', end: '' },
		program: [],
		themes: [],
		gender: [],
		geography: { state: [], country: [] },
		status: [],
		reviewer: [],
		round1Score: { min: '', max: '' },
		round2Score: { min: '', max: '' },
		average: { min: '', max: '' },
		institutions: [],
		degrees: [],
		fieldOfStudy: [],
		graduationYear: { from: '', to: '' },
		// Professional filters
		organizations: [],
		designations: [],
	};

	// State for statistics
	const [stats, setStats] = useState({
		totalApplications: { draft: 0, submitted: 0 },
		review1: { inProgress: 0, completed: 0 },
		review2: { inProgress: 0, completed: 0 },
		shortlisted: 0,
		rejected: 0,
	});

	// State for applications list
	const [applications, setApplications] = useState([]);
	const [filteredApplications, setFilteredApplications] = useState([]);
	const [loading, setLoading] = useState(true);
	const [pageSize, setPageSize] = useState(50);
	const [page, setPage] = useState(1);

	const [pagination, setPagination] = useState({
		total: 0,
		page: 1,
		pageSize: 50,
		totalPages: 1
	});
	const pages = Array.from({ length: pagination.totalPages }, (_, i) => i + 1);
	// Search and filter state
	const [searchKeyword, setSearchKeyword] = useState(() => {
		return sessionStorage.getItem('dashboardSearchKeyword') || '';
	});
	const [filters, setFilters] = useState(() => {
		const saved = sessionStorage.getItem('dashboardFilters');
		if (saved) {
			try {
				const parsed = JSON.parse(saved);
				return {
					...DEFAULT_FILTERS,
					...parsed,
					program: Array.isArray(parsed.program) ? parsed.program : [],
					themes: Array.isArray(parsed.themes) ? parsed.themes : [],
					gender: Array.isArray(parsed.gender) ? parsed.gender : [],
					status: Array.isArray(parsed.status) ? parsed.status : [],
					reviewer: Array.isArray(parsed.reviewer) ? parsed.reviewer : [],
					institutions: Array.isArray(parsed.institutions) ? parsed.institutions : [],
					fieldOfStudy: Array.isArray(parsed.fieldOfStudy) ? parsed.fieldOfStudy : [],
					organizations: Array.isArray(parsed.organizations) ? parsed.organizations : [],
					designations: Array.isArray(parsed.designations) ? parsed.designations : [],
					degrees: Array.isArray(parsed.degrees) ? parsed.degrees : [],
					geography: {
						state: Array.isArray(parsed.geography?.state) ? parsed.geography.state : [],
						country: Array.isArray(parsed.geography?.country) ? parsed.geography.country : [],
					},
					round1Score: {
						min: Number(parsed.round1Score?.min) || DEFAULT_FILTERS.round1Score.min,
						max: Number(parsed.round1Score?.max) || DEFAULT_FILTERS.round1Score.max,
					},
					round2Score: {
						min: Number(parsed.round2Score?.min) || DEFAULT_FILTERS.round2Score.min,
						max: Number(parsed.round2Score?.max) || DEFAULT_FILTERS.round2Score.max,
					},
					average: {
						min: Number(parsed.average?.min) || DEFAULT_FILTERS.average.min,
						max: Number(parsed.average?.max) || DEFAULT_FILTERS.average.max,
					},
					dateRange: {
						start: parsed.dateRange?.start || DEFAULT_FILTERS.dateRange.start,
						end: parsed.dateRange?.end || DEFAULT_FILTERS.dateRange.end,
					},
					graduationYear: {
						from: parsed.graduationYear?.from !== undefined ?
							Number(parsed.graduationYear.from) : DEFAULT_FILTERS.graduationYear.from,
						to: parsed.graduationYear?.to !== undefined ?
							Number(parsed.graduationYear.to) : DEFAULT_FILTERS.graduationYear.to,
					},
				};
			} catch (e) {
				console.warn('Invalid dashboardFilters in sessionStorage, using defaults');
				return DEFAULT_FILTERS;
			}
		}
		return DEFAULT_FILTERS;
	});

	// Sorting state
	const [sortConfig, setSortConfig] = useState(() => {
		const saved = sessionStorage.getItem('dashboardSortConfig');
		if (saved) {
			try {
				const parsed = JSON.parse(saved);
				return parsed;
			} catch (e) {
				console.warn('Invalid dashboardSortConfig in sessionStorage, using defaults');
				return {
					field: 'applicationDate',
					direction: 'desc',
				};
			}
		}
		return {
			field: 'applicationDate',
			direction: 'desc',
		};
	});

	// Available column definitions
	const availableColumns = [
		{ key: 'id', label: 'Application ID', sortable: true, fixed: true },
		{ key: 'select', label: 'Select', sortable: false, fixed: true },
		{ key: 'name', label: 'Name', sortable: true },
		{ key: 'email', label: 'Email', sortable: true },
		{ key: 'phone', label: 'Phone', sortable: false },
		{ key: 'institution', label: 'Institution', sortable: true },
		{ key: 'fieldOfStudy', label: 'Field of Study', sortable: true },
		{ key: 'graduationYear', label: 'Graduation Year', sortable: true },
		{ key: 'organisation', label: 'Organization', sortable: true },
		{ key: 'designation', label: 'Designation', sortable: true },
		{ key: 'applicationDate', label: 'Application Date', sortable: true },
		{ key: 'round1Score', label: 'Round 1 Score', sortable: true },
		{ key: 'round2Score', label: 'Round 2 Score', sortable: true },
		{ key: 'averageScore', label: 'Average Score', sortable: true },
		{ key: 'degrees', label: 'Degree', sortable: true },
		{ key: 'program', label: 'Program Applied For', sortable: true },
		{ key: 'themes', label: 'Selected Themes', sortable: true },
		{ key: 'gender', label: 'Gender', sortable: true },
		{ key: 'state', label: 'State', sortable: true },
		{ key: 'country', label: 'Country', sortable: true },
		{ key: 'status', label: 'Status', sortable: true },
		{ key: 'reviewStatus', label: 'Review Status', sortable: true },
		{ key: 'reviewer', label: 'Reviewer', sortable: true },
		{ key: 'proposalTitle', label: 'Proposal Title', sortable: false },
		{ key: 'files', label: 'Files', sortable: false },
		{ key: 'actions', label: 'Actions', sortable: false, fixed: true },
	];

	// Default visible columns
	const DEFAULT_VISIBLE_COLUMNS = [
		'select',
		'id',
		'name',
		'applicationDate',
		'round1Score',
		'round2Score',
		'averageScore',
		'program',
		'themes',
		'state',
		'status',
		'reviewStatus',
		'reviewer',
		'actions',
	];

	// Column management state
	const [visibleColumns, setVisibleColumns] = useState(() => {
		const saved = sessionStorage.getItem('dashboardColumns');
		if (saved) {
			try {
				const parsed = JSON.parse(saved);
				// Validate: ensure only valid keys and fixed columns are included
				const validKeys = availableColumns.map(col => col.key);
				const fixedKeys = availableColumns.filter(col => col.fixed).map(col => col.key);
				const filtered = parsed.filter(key => validKeys.includes(key));
				// Ensure fixed columns are always included
				const withFixed = [...filtered];
				fixedKeys.forEach(key => {
					if (!withFixed.includes(key)) {
						withFixed.push(key);
					}
				});
				// Ensure select and id at start, actions at end
				const columnsWithoutFixed = withFixed.filter(key => key !== 'select' && key !== 'id' && key !== 'actions');
				const hasSelect = withFixed.includes('select');
				const hasId = withFixed.includes('id');
				const hasActions = withFixed.includes('actions');
				
				let finalColumns = [];
				if (hasSelect) finalColumns.push('select');
				if (hasId) finalColumns.push('id');
				finalColumns = [...finalColumns, ...columnsWithoutFixed];
				if (hasActions) finalColumns.push('actions');
				
				return finalColumns;
			} catch (e) {
				console.warn('Invalid dashboardColumns in sessionStorage, using defaults');
				return DEFAULT_VISIBLE_COLUMNS;
			}
		}
		return DEFAULT_VISIBLE_COLUMNS;
	});

	// Selection state
	const [selectedApplications, setSelectedApplications] = useState([]);
	const [selectAll, setSelectAll] = useState(false);

	// Modal states
	const [showFilterPanel, setShowFilterPanel] = useState(false);
	const [showColumnManager, setShowColumnManager] = useState(false);
	const [showExportModal, setShowExportModal] = useState(false);
	const [showSaveDashboardModal, setShowSaveDashboardModal] = useState(false);
	const [showShareDashboardModal, setShowShareDashboardModal] = useState(false);
	const [showFinalizeModal, setShowFinalizeModal] = useState(false);
	const [showRound2AssignmentModal, setShowRound2AssignmentModal] = useState(false);
	const [showAcceptApplicationsModal, setShowAcceptApplicationsModal] = useState(false);
	const [showSendAnnexureModal, setShowSendAnnexureModal] = useState(false);
	const [dashboardToShare, setDashboardToShare] = useState(null);
	const [pageSizeDropdownOpen, setPageSizeDropdownOpen] = useState(false);
	const [dashboardState, setDashboardState] = useState(null);
	// 3. Add error state to your component
const [errorState, setErrorState] = useState({ hasError: false, message: '' });
	// Filter options fetched from backend (programs, categories, genders, states, countries, themes, reviewers)
	const [filterOptions, setFilterOptions] = useState({
		programs: [],
		statuses: [],
		reviewStatuses: [],
		states: [],
		countries: [],
		genders: [],
		themes: [],
		reviewers: [],
		degrees: [],
		institutions: [],
		fieldOfStudy: [],
		organizations: [],
		designations: [],
		graduationYear: [],
		round1Score: [],
		round2Score:[],
		average: [],
		
	});

	const fetchFilterOptions = async () => {
		try {
			const token = sessionStorage.getItem('token');
			const response = await axios.get(`${BASE_URL}/api/admin/dashboard/applications/filters/options`, {
				headers: { Authorization: `Bearer ${token}` },
			});
			if (response.data.success) {
				setFilterOptions(response.data.data || {});
				console.log("filterOptions", filterOptions);
				console.log("response.data.data", response.data.data);

			}
		} catch (err) {
			console.error('Error fetching filter options:', err);
		}
	};

	// Fetch dashboard statistics
	const fetchStats = async () => {
		try {
			const token = sessionStorage.getItem('token');
			const response = await axios.get(`${BASE_URL}/api/admin/dashboard/stats`, {
				headers: { Authorization: `Bearer ${token}` },
			});

			if (response.data.success) {
				setStats(response.data.data);
			}
		} catch (error) {
			console.error('Error fetching stats:', error);
			toast.error('Failed to load statistics');
		}
	};

	// Fetch applications
	const fetchApplications = async () => {
		setLoading(true);
		try {
			const token = sessionStorage.getItem('token');
			const response = await axios.get(`${BASE_URL}/api/admin/dashboard/applications`, {
				headers: { Authorization: `Bearer ${token}` },
				params: {
					filters: JSON.stringify(filters),
					sortConfig: JSON.stringify(sortConfig),
					pageSize: pageSize,
					searchKeyword: searchKeyword,
					page: page,
				}
			});
			const res = response.data.data;
			//console.log("setFilteredApplications",response.data.data);
			if (response.data.success) {
				setApplications(res.formattedApplications);
				console.log("setApplications", res.formattedApplications);
				setFilteredApplications(res.formattedApplications);
				setPagination(res.pagination);
			}
		} catch (error) {
			console.error('Error fetching applications:', error);
			toast.error('Failed to load applications');
		} finally {
			setLoading(false);
		}
	};

	// Initial data fetch
	useEffect(() => {
		fetchStats();
		fetchApplications();
		fetchFilterOptions();
	}, []);

	useEffect(() => {
		fetchApplications();
	}, [page, pageSize, filters, searchKeyword, sortConfig]);
	// Save filters to sessionStorage when they change
	useEffect(() => {
		sessionStorage.setItem('dashboardFilters', JSON.stringify(filters));
	}, [filters]);

	// Save searchKeyword to sessionStorage when it changes
	useEffect(() => {
		sessionStorage.setItem('dashboardSearchKeyword', searchKeyword);
	}, [searchKeyword]);

	// Save sortConfig to sessionStorage when it changes
	useEffect(() => {
		sessionStorage.setItem('dashboardSortConfig', JSON.stringify(sortConfig));
	}, [sortConfig]);

	// 🚀 Load shared dashboard (URL: ?state=ID)
// 3. Update the initial load effect
useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const stateId = params.get("state");
    console.log("stateId", stateId);
    
    const loadInitialData = async () => {
        setLoading(true);
        setErrorState({ hasError: false, message: '' });
        
        try {
            if (stateId) {
                const result = await loadSharedDashboardState(stateId);
                    if (result.success) {
						fetchStats();
						fetchApplications();
						fetchFilterOptions();
        		console.log("result", result);
				setDashboardState(result.data);
		 			window.dispatchEvent(
				new CustomEvent('applyDashboardState', { detail: {
                ...result.data
            } })
			);
    } else {
        setErrorState({
            hasError: true,
            message: "You don't have access to this dashboard or it doesn't exist."
        });
    }
            } else {
                setDashboardState(getCurrentDashboardState());
            }
        } catch (error) {
            setErrorState({
                hasError: true,
                message: "An error occurred while loading the dashboard."
            });
        } finally {
            setLoading(false);
        }
    };

    loadInitialData();
}, []);

	// 🔥 Load Saved/Shared Dashboard State
// 2. Update the loadSharedDashboardState function
const loadSharedDashboardState = async (id) => {
    try {
        const token = sessionStorage.getItem('token');
        const res = await axios.get(`${BASE_URL}/api/admin/dashboard/shared-dashboard/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        
        if (res.data?.success ) {
            const dashboardResponse = res.data.data.dashboard;
           // setDashboardState(parsed);
            return { success: true, data: dashboardResponse };
        } else {
            console.error("Unexpected response format:", res.data);
            return { success: false, error: "Invalid dashboard format" };
        }
    } catch (err) {
        console.error("Error loading dashboard:", {
            message: err.message,
            response: err.response?.data,
            status: err.response?.status
        });
        return { 
            success: false, 
            error: err.response?.data?.message || "Failed to load dashboard" 
        };
    }
};





	// Event listeners for dashboard state management
	useEffect(() => {
		// Listen for apply dashboard state event from SaveDashboardModal
		const handleApplyDashboardState = (event) => {
			console.log("handleApplyDashboardState", event);
			const dashboard = event.detail;
			console.log("handleApplyDashboardState dashboard", dashboard);
			setDashboardState(dashboard);
			const state = dashboard.state;
			// Normalize incoming filters from saved dashboards. Saved state may come from DB
			// and could contain strings or differently-shaped values.
			if (state.filters) {
				let incoming = state.filters;
				// If filters stored as JSON string, parse it
				if (typeof incoming === 'string') {
					try {
						incoming = JSON.parse(incoming);
					} catch (err) {
						console.warn('Could not parse filters JSON from saved dashboard', err);
					}
				}
				const normalizeArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);
				const nf = {
					...DEFAULT_FILTERS,
					...incoming,
					program: normalizeArray(incoming.program),
					themes: normalizeArray(incoming.themes),
					gender: normalizeArray(incoming.gender),
					status: normalizeArray(incoming.status),
					reviewer: normalizeArray(incoming.reviewer),
					degrees: normalizeArray(incoming.degrees),
					institutions: normalizeArray(incoming.institutions),
					fieldOfStudy: normalizeArray(incoming.fieldOfStudy),
					organizations: normalizeArray(incoming.organizations),
					designations: normalizeArray(incoming.designations),
					geography: {
						state: normalizeArray(incoming.geography?.state),
						country: normalizeArray(incoming.geography?.country),
					},
					round1Score: {
						min: Number(incoming.round1Score?.min) || DEFAULT_FILTERS.round1Score.min,
						max: Number(incoming.round1Score?.max) || DEFAULT_FILTERS.round1Score.max,
					},
					round2Score: {
						min: Number(incoming.round2Score?.min) || DEFAULT_FILTERS.round2Score.min,
						max: Number(incoming.round2Score?.max) || DEFAULT_FILTERS.round2Score.max,
					},
					average: {
						min: Number(incoming.average?.min) || DEFAULT_FILTERS.average.min,
						max: Number(incoming.average?.max) || DEFAULT_FILTERS.average.max,
					},
					dateRange: {
						start: incoming.dateRange?.start || DEFAULT_FILTERS.dateRange.start,
						end: incoming.dateRange?.end || DEFAULT_FILTERS.dateRange.end,
					},
					graduationYear: {
						from: Number(incoming.graduationYear?.from || DEFAULT_FILTERS.graduationYear.from),
						to: Number(incoming.graduationYear?.to || DEFAULT_FILTERS.graduationYear.to),
					}
				};
				setFilters(nf);
			}
			if (state.visibleColumns) {
				// Ensure actions column is always at the end when loading saved state
				const columnsWithoutActions = state.visibleColumns.filter(key => key !== 'actions');
				const hasActions = state.visibleColumns.includes('actions');
				const finalColumns = hasActions ? [...columnsWithoutActions, 'actions'] : state.visibleColumns;
				setVisibleColumns(finalColumns);
			}
			if (state.sortConfig) {
				setSortConfig(state.sortConfig);
			}
			if (state.searchKeyword !== undefined) {
				setSearchKeyword(state.searchKeyword);
			}
		};

		// Listen for open share modal event from SaveDashboardModal
		const handleOpenShareModal = (event) => {
			const dashboard = event.detail;
			setDashboardToShare(dashboard);
			setShowShareDashboardModal(true);
		};

		// Listen for open save modal event

		window.addEventListener('applyDashboardState', handleApplyDashboardState);
		window.addEventListener('openShareModal', handleOpenShareModal);

		return () => {
			window.removeEventListener('applyDashboardState', handleApplyDashboardState);
			window.removeEventListener('openShareModal', handleOpenShareModal);
		};
	}, []);



	// Apply sorting
	const sortedApplications = React.useMemo(() => {
		let sorted = [...filteredApplications];

		if (sortConfig.field) {
			sorted.sort((a, b) => {
				let aValue = a[sortConfig.field];
				let bValue = b[sortConfig.field];

				// Handle different data types
				if (sortConfig.field === 'applicationDate') {
					aValue = new Date(aValue);
					bValue = new Date(bValue);
				} else if (typeof aValue === 'string') {
					aValue = aValue.toLowerCase();
					bValue = bValue?.toLowerCase() || '';
				}

				if (aValue < bValue) {
					return sortConfig.direction === 'asc' ? -1 : 1;
				}
				if (aValue > bValue) {
					return sortConfig.direction === 'asc' ? 1 : -1;
				}
				return 0;
			});
		}

		return sorted;
	}, [filteredApplications, sortConfig]);

	// Pagination
	const paginatedApplications = React.useMemo(() => {
		const startIndex = (page - 1) * pageSize;
		const endIndex = startIndex + pageSize;
		return sortedApplications.slice(startIndex, endIndex);
	}, [sortedApplications, page, pageSize]);


	// Handle sort
	const handleSort = (field) => {
		if (!availableColumns.find((col) => col.key === field)?.sortable) return;

		setSortConfig((prev) => ({
			field,
			direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
		}));
	};

	// Handle column visibility change
	const handleColumnChange = (columns) => {
		// Ensure select and id are at start, actions is at end
		const columnsWithoutFixed = columns.filter(key => key !== 'select' && key !== 'id' && key !== 'actions');
		const hasSelect = columns.includes('select');
		const hasId = columns.includes('id');
		const hasActions = columns.includes('actions');
		
		let finalColumns = [];
		if (hasSelect) finalColumns.push('select');
		if (hasId) finalColumns.push('id');
		finalColumns = [...finalColumns, ...columnsWithoutFixed];
		if (hasActions) finalColumns.push('actions');
		
		setVisibleColumns(finalColumns);
		sessionStorage.setItem('dashboardColumns', JSON.stringify(finalColumns));
	};

	// Handle select all
	const handleSelectAll = () => {
		if (selectAll) {
			setSelectedApplications([]);
		} else {
			setSelectedApplications(paginatedApplications.map((app) => app.id));
		}
		setSelectAll(!selectAll);
	};

	// Handle individual selection
	const handleSelect = (id) => {
		setSelectedApplications((prev) => {
			if (prev.includes(id)) {
				return prev.filter((appId) => appId !== id);
			} else {
				return [...prev, id];
			}
		});
	};

	// Handle batch export
	const handleBatchExport = async () => {
		if (selectedApplications.length === 0) {
			toast.warning('Please select applications to export');
			return;
		}
		setShowExportModal(true);
	};

	// Get current dashboard state
	const getCurrentDashboardState = () => {
		return {
			label: dashboardState?.label || '',
			filters,
			visibleColumns,
			sortConfig,
			searchKeyword,
			pageSize,
		};
	};

	// Handle save dashboard
	const handleSaveDashboard = () => {
		setShowSaveDashboardModal(true);
	};

	// Handle share dashboard
	const handleShareDashboard = () => {
		setDashboardToShare(null);
		setShowShareDashboardModal(true);
	};

	// Get sort icon
	const getSortIcon = (field) => {
		if (sortConfig.field !== field) return '⇅';
		return sortConfig.direction === 'asc' ? '↑' : '↓';
	};
// 4. Update the return statement at the bottom of your component
if (loading) {
    return (
        <div className="loading-spinner">
            <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading dashboard...</p>
        </div>
    );
}

if (errorState.hasError) {
    return (
        <div className="container mt-5">
            <div className="alert alert-danger">
                <h3>Error Loading Dashboard</h3>
                <p className="mb-3">{errorState.message}</p>
                <button 
                    className="btn btn-primary"
                    onClick={() => (window.location.href = '/admin/dashboard')}
                >
                    Back to Dashboard
                </button>
            </div>
        </div>
    );
}
	return (
		
		<div className="container">
			<Container fluid>
				{/* Compact Header - All in One Row */}
				<div className="compact-header mb-2">
					<div className="header-left">
						<h1 className="dashboard-title-compact">
							<i className="bi bi-speedometer2 me-1"></i>
							{dashboardState?.label || 'Admin Dashboard'}
						</h1>
						<span className="dashboard-subtitle">Manage fellowship applications and review process</span>
					</div>
					<div className="header-actions">
						<div className="action-buttons-compact">
							<span className="button-group-label-sm">Review</span>
							<Button color="info" onClick={() => setShowRound2AssignmentModal(true)} size="sm" className="btn-compact">
								<i className="bi bi-person-plus me-1"></i>
								Assign Round 2
							</Button>
							<Button color="warning" onClick={() => setShowFinalizeModal(true)} size="sm" className="btn-compact">
								<i className="bi bi-check-circle me-1"></i>
								Shortlist Application
							</Button>
							<span className="button-group-label-sm ms-2">Actions</span>
							<Button color="primary" onClick={() => setShowSendAnnexureModal(true)} size="sm" className="btn-compact">
								<i className="bi bi-envelope me-1"></i>
								Send Annexure Email
							</Button>
							<Button color="success" onClick={() => setShowAcceptApplicationsModal(true)} size="sm" className="btn-compact">
								<i className="bi bi-file-earmark-check me-1"></i>
								Finalize Candidates
							</Button>
						</div>
						<div className="utility-buttons">
							<Button color="primary" onClick={handleSaveDashboard} size="sm" className="btn-icon" title="Save Dashboard">
								<i className="bi bi-save"></i>
							</Button>
							<Button color="info" onClick={handleShareDashboard} size="sm" className="btn-icon" title="Share Dashboard">
								<i className="bi bi-share"></i>
							</Button>
						</div>
					</div>
				</div>				{/* Compact Statistics Cards */}
				<div className="stats-section-compact mb-2">
					<Row className="g-2">
						<Col lg={6} xl={3}>
							<StatsCard
								title="Total Applications"
								icon="bi bi-file-earmark-text"
								iconColor="primary"
								stats={[
									{ label: 'Draft', value: stats.totalApplications.draft, color: 'warning' },
									{ label: 'Submitted', value: stats.totalApplications.submitted, color: 'success' },
								]}
							/>
						</Col>
						<Col lg={6} xl={3}>
							<StatsCard
								title="Review Round 1"
								icon="bi bi-clipboard-check"
								iconColor="info"
								stats={[
									{ label: 'In Progress', value: stats.review1.inProgress, color: 'primary' },
									{ label: 'Completed', value: stats.review1.completed, color: 'success' },
								]}
							/>
						</Col>
						<Col lg={6} xl={3}>
							<StatsCard
								title="Review Round 2"
								icon="bi bi-clipboard2-check"
								iconColor="secondary"
								stats={[
									{ label: 'In Progress', value: stats.review2.inProgress, color: 'primary' },
									{ label: 'Completed', value: stats.review2.completed, color: 'success' },
								]}
							/>
						</Col>
						<Col lg={6} xl={3}>
							<StatsCard
								title="Final Status"
								icon="bi bi-award"
								iconColor="success"
								stats={[
									{ label: 'Shortlisted', value: stats.shortlisted, color: 'success' },
									{ label: 'Rejected', value: stats.rejected, color: 'danger' },
								]}
							/>
						</Col>
					</Row>
				</div>

				{/* Controls Row */}
				<Row className="mb-3">
					<Col md={4}>
						<div className="search-box">
							<Input
								type="text"
								placeholder="Search by name, email, institution, proposal title..."
								value={searchKeyword}
								onChange={(e) => setSearchKeyword(e.target.value)}
								className="search-input"
							/>

						</div>
					</Col>
					<Col md={8} className="text-end">
						<Button
							color="info"
							outline
							onClick={() => {
								fetchApplications();
								fetchStats();
								toast.success('Dashboard data refreshed');
							}}
							className="me-2"
							title="Reload data"
						>
							<i className="bi bi-arrow-clockwise"></i>
						</Button>
						<Button color="primary" outline onClick={() => setShowFilterPanel(true)} className="me-2">
							Filters {Object.values(filters).some(f => Array.isArray(f) ? f.length > 0 : (f.start || f.min > 0)) && <Badge color="danger" pill>•</Badge>}
						</Button>
						<Button color="secondary" outline onClick={() => setShowColumnManager(true)} className="me-2">
							Columns
						</Button>
						<Button
							color="success"
							onClick={handleBatchExport}
							disabled={selectedApplications.length === 0}>
							Export ({selectedApplications.length})
						</Button>
					</Col>
				</Row>

				{/* Applications Table */}
				<Row>
					<Col>
						<Card>
							<CardBody className="p-0">
								{loading ? (
									<div className="text-center p-5">
										<Spinner color="primary" />
										<p className="mt-3">Loading applications...</p>
									</div>
								) : (
									<div className="table-responsive">
										<Table hover className="dashboard-table mb-0">
											<thead>
												<tr>
													{visibleColumns.map((colKey) => {
														const col = availableColumns.find((c) => c.key === colKey);
														if (!col) return null;
														if (col.key === 'select') {
															return (
																<th key={col.key} className="text-center">
																	<Input type="checkbox" checked={selectAll} onChange={handleSelectAll} />
																</th>
															);
														}
														return (
															<th
																key={col.key}
																onClick={() => col.sortable && handleSort(col.key)}
																className={col.sortable ? 'sortable' : ''}
																style={{ cursor: col.sortable ? 'pointer' : 'default' }}>
																{col.label}
																{col.sortable && (
																	<span className="sort-icon ms-1">{getSortIcon(col.key)}</span>
																)}
															</th>
														);
													})}
												</tr>
											</thead>
											<tbody>
												{paginatedApplications.length === 0 ? (
													<tr>
														<td colSpan={visibleColumns.length} className="text-center p-5">
															<div className="no-data">
																<span className="no-data-icon">📭</span>
																<p>No applications found</p>
															</div>
														</td>
													</tr>
												) : (
													paginatedApplications.map((app) => (
														<ApplicationRow
															key={app.id}
															application={app}
															visibleColumns={visibleColumns}
															isSelected={selectedApplications.includes(app.id)}
															onSelect={handleSelect}
															onUpdate={() => {
																fetchApplications();
																fetchStats();
															}}
														/>
													))
												)}
											</tbody>
										</Table>
									</div>
								)}
							</CardBody>
						</Card>
					</Col>
				</Row>
				<Row className="mt-3 align-items-center">
					{/* Left: Total Records */}
					<Col md={4} className="text-start">
						<div className="results-summary-bottom">
							Showing {(pagination.page - 1) * pagination.pageSize + 1} -
							{Math.min(pagination.page * pagination.pageSize, pagination.total)} of {pagination.total} applications
						</div>
					</Col>

					{/* Center: Pagination */}
					<Col md={4} className="d-flex justify-content-center">
						<div className="pagination">
							<button
								className="btn btn-secondary btn-sm me-1"
								disabled={page === 1}
								onClick={() => setPage(page - 1)}
							>
								Prev
							</button>

							{pages.map((p) => (
								<button
									key={p}
									className={`btn btn-sm mx-1 ${page === p ? "btn-primary" : "btn-outline-primary"}`}
									onClick={() => setPage(p)}
								>
									{p}
								</button>
							))}

							<button
								className="btn btn-secondary btn-sm ms-1"
								disabled={page === pagination.totalPages}
								onClick={() => setPage(page + 1)}
							>
								Next
							</button>
						</div>
					</Col>

					{/* Right: Page Size Selector */}
					<Col md={4} className="d-flex justify-content-end">
						<select
							className="form-select w-auto"
							value={pageSize}
							onChange={(e) => {
								setPageSize(Number(e.target.value));
								setPage(1); // Reset page
							}}
						>
							<option value={50}>50</option>
							<option value={100}>100</option>
							<option value={500}>500</option>
							<option value={100}>1000</option>
						</select>
					</Col>
				</Row>


				{/* Filter Panel Modal */}
				<FilterPanel
					isOpen={showFilterPanel}
					toggle={() => setShowFilterPanel(false)}
					filters={filters}
					onFilterChange={setFilters}
					options={filterOptions}
				/>

				{/* Column Manager Modal */}
				<ColumnManager
					isOpen={showColumnManager}
					toggle={() => setShowColumnManager(false)}
					availableColumns={availableColumns}
					visibleColumns={visibleColumns}
					onColumnChange={handleColumnChange}
					defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
				/>

				{/* Export Modal */}
				<ExportModal
					isOpen={showExportModal}
					toggle={() => setShowExportModal(false)}
					selectedApplications={selectedApplications}
					applications={applications}
					onExportComplete={() => {
						setSelectedApplications([]);
						setSelectAll(false);
					}}
				/>

				{/* Save Dashboard Modal */}
				<SaveDashboardModal
					isOpen={showSaveDashboardModal}
					toggle={() => setShowSaveDashboardModal(false)}
					currentDashboardState={getCurrentDashboardState()}
				/>

				{/* Share Dashboard Modal */}
				<ShareDashboardModal
					isOpen={showShareDashboardModal}
					toggle={() => {
						setShowShareDashboardModal(false);
						setDashboardToShare(null);
					}}
					dashboardToShare={dashboardToShare}
					currentDashboardState={getCurrentDashboardState()}
				/>

			{/* Round 2 Assignment Modal */}
			<Round2AssignmentModal
				isOpen={showRound2AssignmentModal}
				toggle={() => setShowRound2AssignmentModal(false)}
				onAssignmentComplete={() => {
					// Refresh stats and applications after Round 2 assignment
					fetchStats();
					fetchApplications();
				}}
			/>

			{/* Shortlist Application Modal */}
			<FinalizeCandidatesModal
				isOpen={showFinalizeModal}
				toggle={() => setShowFinalizeModal(false)}
				onFinalized={() => {
					// Refresh stats and applications after finalization
					fetchStats();
					fetchApplications();
					toast.success('Applications shortlisted successfully');
				}}
			/>

			{/* Send Annexure Email Modal */}
			<SendAnnexureModal
				isOpen={showSendAnnexureModal}
				toggle={() => setShowSendAnnexureModal(false)}
				onEmailsSent={() => {
					// Refresh stats and applications after sending emails
					fetchStats();
					fetchApplications();
					toast.success('Annexure emails sent successfully');
				}}
			/>

			{/* Finalize Candidates Modal */}
			<AcceptApplicationsModal
				isOpen={showAcceptApplicationsModal}
				toggle={() => setShowAcceptApplicationsModal(false)}
				onAccepted={() => {
					// Refresh stats and applications after acceptance
					fetchStats();
					fetchApplications();
				}}
			/>
		</Container>		</div>
		
	);
	

};

export default Dashboard;